// 5 |
// 4 |         * (4,4)
// 3 |
// 2 |
// 1 |________________
// 0  1  2  3  4  5 
export class Vector {
    readonly x: number;
    readonly y: number;
    readonly z: number;

    constructor(x: number, y: number, z: number) {
        this.x = x;
        this.y = y;
        this.z = z;
    }

    static add(vector1: Vector, vector2: Vector): Vector {

        return new Vector(vector1.x + vector2.x, vector1.y + vector2.y, vector1.z + vector2.z);
    }

    static subtract(vector1: Vector, vector2: Vector): Vector {
        return new Vector(vector1.x - vector2.x, vector1.y - vector2.y, vector1.z - vector2.z);
    }

    static scale(vector: Vector, factor: number): Vector {
        return new Vector(vector.x * factor, vector.y * factor, vector.z * factor);
    }

    //
    // v1.v2
    //
    static dotProduct(vector1: Vector, vector2: Vector): number {
        //The dot product of 2 vectors A(a1,a2,a3) and B(b1,b2,b3) is given by the next
        //identity:
        //A·B = a1*b1 + a2*b2 + a3*b3
        //A.A = a1*a1 + a2*a2 + a3*a3
        //A.A = ||A||^2
        //
        //A.A is effectively pythagoras
        //  c*c = a*a + b*b
        return vector1.x * vector2.x + vector1.y * vector2.y + vector1.z * vector2.z;
    }

    //
    // ||v|| denotes the norm, magnitude or length of a vector = sqrt(v.v)
    // i.e. the distance from the origin to the point represented by the vector
    //
    static magnitude(vector: Vector): number {
        return Math.sqrt(Vector.dotProduct(vector, vector));
    }

    // gets a vector of magnitude 1
    static normalise(vector: Vector): Vector {
        const size = Vector.magnitude(vector);
        if (size < 1e-12) {
            console.warn(`Attempting to normalise a vector with near zero magnitude ${size}, returning zero vector instead`);
            return new Vector(0, 0, 0);
        }
        return new Vector(vector.x / size, vector.y / size, vector.z / size);
    }

    // Projects a vector onto a plane, removing the component along the plane normal
    // Returns only the tangential component (parallel to the plane)
    static projectOntoPlane(vector: Vector, planeNormal: Vector): Vector {
        const alongNormal = Vector.dotProduct(vector, planeNormal);
        return Vector.subtract(vector, Vector.scale(planeNormal, alongNormal));
    }

    // Removes the normal component only if moving toward the plane (negative dot product)
    // Used for collision response to prevent penetration while allowing sliding
    static removeNormalComponentIfToward(vector: Vector, planeNormal: Vector): Vector {
        const alongNormal = Vector.dotProduct(vector, planeNormal);
        if (alongNormal < 0) {
            return Vector.subtract(vector, Vector.scale(planeNormal, alongNormal));
        }
        return vector;
    }

}